import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { apiService } from '../services/api';

interface FantasyLeague {
  id: number;
  name: string;
  league_type: string;
  live_tournament_id: number;
  tournament_name: string;
  max_participants: number;
  current_participants: number;
  status: string;
  is_public: boolean;
  join_code?: string;
  created_by: number;
  created_at: string;
  is_creator: boolean;
}

const BudgetLeagueOverview: React.FC = () => {
  const [leagues, setLeagues] = useState<FantasyLeague[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    checkAuthStatus();
    fetchBudgetLeagues();
  }, []);

  const checkAuthStatus = () => {
    const token = localStorage.getItem('authToken');
    setIsLoggedIn(!!token);
  };

  const fetchBudgetLeagues = async () => {
    try {
      setLoading(true);
      setError(null);

      const data = await apiService.getFantasyLeagues();

      if (data.success) {
        const budgetLeagues = data.tournaments.filter(
          (league: FantasyLeague) => league.league_type === 'budget_league'
        );
        setLeagues(budgetLeagues);
      } else {
        setError(data.message || 'Failed to load leagues');
      }
    } catch (err) {
      setError('Failed to load leagues');
      console.error('Error fetching budget leagues:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateLeague = () => {
    if (!isLoggedIn) {
      navigate('/tournaments');
      return;
    }
    navigate('/create-league?type=budget_league');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto py-8 px-4">
      {/* Header */}
      <div className="text-center mb-12">
        <div className="text-6xl mb-4">💰</div>
        <h1 className="text-4xl font-bold text-white mb-4">Budget League Tournaments</h1>
        <p className="text-xl text-white/80 max-w-3xl mx-auto">
          Build your dream team within budget constraints! Every player has a price tag, 
          and you must stay within your allocated budget to create the ultimate squad.
        </p>
      </div>

      {/* How It Works */}
      <div className="bg-white/10 backdrop-blur-md rounded-lg p-8 border border-white/20 mb-8">
        <h2 className="text-2xl font-bold text-white mb-6">How Budget League Works</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="text-center">
            <div className="bg-green-600 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-2xl">💳</span>
            </div>
            <h3 className="text-lg font-semibold text-white mb-2">1. Manage Budget</h3>
            <p className="text-white/70">You have a fixed budget to spend on players. Each player has a price based on their performance.</p>
          </div>
          <div className="text-center">
            <div className="bg-blue-600 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-2xl">👥</span>
            </div>
            <h3 className="text-lg font-semibold text-white mb-2">2. Build Team</h3>
            <p className="text-white/70">Select players strategically to maximize your team's potential while staying within budget.</p>
          </div>
          <div className="text-center">
            <div className="bg-purple-600 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-2xl">📊</span>
            </div>
            <h3 className="text-lg font-semibold text-white mb-2">3. Compete & Win</h3>
            <p className="text-white/70">Your team earns points based on real player performances. The best team wins!</p>
          </div>
        </div>
      </div>

      {/* Key Features */}
      <div className="bg-white/10 backdrop-blur-md rounded-lg p-8 border border-white/20 mb-8">
        <h2 className="text-2xl font-bold text-white mb-6">Key Features</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <h3 className="text-lg font-semibold text-white mb-3">Budget Management</h3>
            <ul className="space-y-2 text-white/80">
              <li>• <strong>Fixed Budget:</strong> Every team has the same starting budget</li>
              <li>• <strong>Dynamic Pricing:</strong> Player prices change based on form and demand</li>
              <li>• <strong>Transfer Window:</strong> Make changes during designated periods</li>
              <li>• <strong>Budget Tracking:</strong> Real-time budget monitoring</li>
            </ul>
          </div>
          <div>
            <h3 className="text-lg font-semibold text-white mb-3">Team Building</h3>
            <ul className="space-y-2 text-white/80">
              <li>• <strong>Player Categories:</strong> Batsmen, Bowlers, All-rounders, Wicket-keepers</li>
              <li>• <strong>Form Analysis:</strong> Recent performance indicators</li>
              <li>• <strong>Fixture Planning:</strong> Consider upcoming matches</li>
              <li>• <strong>Captain Selection:</strong> Double points for your captain</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Scoring System */}
      <div className="bg-white/10 backdrop-blur-md rounded-lg p-8 border border-white/20 mb-8">
        <h2 className="text-2xl font-bold text-white mb-6">Scoring System</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <h3 className="text-lg font-semibold text-white mb-3">Batting Points</h3>
            <ul className="space-y-2 text-white/80">
              <li>• <strong>Runs:</strong> 1 point per run</li>
              <li>• <strong>Boundaries:</strong> +1 bonus for fours, +2 for sixes</li>
              <li>• <strong>Milestones:</strong> +10 for 50, +20 for 100</li>
              <li>• <strong>Strike Rate:</strong> Bonus for high strike rates</li>
            </ul>
          </div>
          <div>
            <h3 className="text-lg font-semibold text-white mb-3">Bowling Points</h3>
            <ul className="space-y-2 text-white/80">
              <li>• <strong>Wickets:</strong> 10 points per wicket</li>
              <li>• <strong>Maiden Overs:</strong> +5 bonus points</li>
              <li>• <strong>Economy Rate:</strong> Bonus for low economy</li>
              <li>• <strong>Hat-tricks:</strong> +25 bonus points</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Available Leagues */}
      {leagues.length > 0 && (
        <div className="bg-white/10 backdrop-blur-md rounded-lg p-8 border border-white/20 mb-8">
          <h2 className="text-2xl font-bold text-white mb-6">Available Budget Leagues</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {leagues.map((league) => (
              <div key={league.id} className="bg-white/5 rounded-lg p-6 border border-white/10 hover:border-white/20 transition-all">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-white">{league.name}</h3>
                  <span className="text-sm text-white/60">💰</span>
                </div>
                <p className="text-white/70 mb-4">{league.tournament_name}</p>
                <div className="space-y-2 mb-4">
                  <div className="flex justify-between text-sm">
                    <span className="text-white/60">Participants:</span>
                    <span className="text-white">{league.current_participants}/{league.max_participants}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-white/60">Status:</span>
                    <span className={`font-semibold ${
                      league.status === 'active' ? 'text-green-400' : 
                      league.status === 'upcoming' ? 'text-yellow-400' : 'text-red-400'
                    }`}>
                      {league.status}
                    </span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-white/60">Type:</span>
                    <span className="text-white">{league.is_public ? 'Public' : 'Private'}</span>
                  </div>
                </div>
                <div className="flex space-x-2">
                  <Link
                    to={`/budget-league/${league.id}`}
                    className="flex-1 bg-blue-600 hover:bg-blue-700 text-white text-center py-2 px-4 rounded-lg text-sm font-semibold transition-all"
                  >
                    View League
                  </Link>
                  {!league.is_creator && (
                    <button
                      onClick={() => navigate(`/budget-league/${league.id}`)}
                      className="bg-green-600 hover:bg-green-700 text-white py-2 px-4 rounded-lg text-sm font-semibold transition-all"
                    >
                      Join
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Action Buttons */}
      <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center mb-8">
        <button
          onClick={handleCreateLeague}
          className="bg-gradient-to-r from-green-600 to-blue-600 hover:from-green-700 hover:to-blue-700 text-white px-4 sm:px-8 py-2 sm:py-3 rounded-lg text-sm sm:text-lg font-semibold transition-all transform hover:scale-105"
        >
          Create Budget League
        </button>
        <Link
          to="/tournaments"
          className="bg-white/10 hover:bg-white/20 text-white px-4 sm:px-8 py-2 sm:py-3 rounded-lg text-sm sm:text-lg font-semibold border border-white/20 transition-all text-center"
        >
          Browse All Tournaments
        </Link>
      </div>
    </div>
  );
};

export default BudgetLeagueOverview; 